import { faFilePdf, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatDate } from 'date-fns'
import React from 'react'
import { useNavigate } from 'react-router'

import ActionButton from '@/components/misc/ActionButton'
import ContactCompanyDropdown from '@/components/misc/CompanyProfile/components/CompanyProfileHeader/ContactCompanyDropdown'
import PendingActionNote from '@/components/misc/CompanyProfile/components/CompanyProfileHeader/PendingActionNote'
import ToggleInternalRelationship from '@/components/misc/CompanyProfile/components/CompanyProfileHeader/ToggleInternalRelationship'
import { COMPANY_PROFILE_TAB } from '@/components/misc/CompanyProfile/constants'
import { useCompanyProfileModal } from '@/components/misc/CompanyProfile/hooks/useCompanyProfileModal'
import ReferClientButton from '@/components/misc/ReferClientButton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAddToProfessionalNetwork } from '@/hooks/useAddToProfessionalNetwork'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { DATE_FORMATS, PANEL_STATUS } from '@/lib/constants'
import { faExclamationTriangle } from '@/lib/fontAwesomePro/light'
import { ROUTES } from '@/lib/routes'
import panelService from '@/lib/services/panelService'
import { cn } from '@/lib/utils'
import { REFERRAL_METHOD } from '@/redux/slices/webReferral'
import { CompanyProfileItem } from '@/types/responses/companies'

interface Props {
  companyProfile: CompanyProfileItem
}

const CompanyActions: React.FC<Props> = ({ companyProfile }) => {
  const currentCompany = useCurrentCompanyOrFail()
  const reportSrc =
    companyProfile.company &&
    companyProfile.company.companyDetail.ifa?.reportUrl
  const { directory, company, panel } = companyProfile
  const panelStatus = panel?.status
  const isCompanyAlreadyInPanel = !!panelStatus
  const companyProfileModal = useCompanyProfileModal()

  const panelStatusStyles = panelStatus
    ? panelService.getPanelStatusStyles({
        panelStatus,
        textType: 'long',
        pendingActions: panel.pendingActions,
      })
    : undefined

  const navigate = useNavigate()

  const { addToProfessionalNetwork, isAdding } = useAddToProfessionalNetwork({
    // The relationship exists now, so the agreement template is reviewed on the
    // tab that lists it.
    onAgreementTemplateReviewNeeded: () =>
      companyProfileModal.goToTab(COMPANY_PROFILE_TAB.commercialAgreements),
  })

  const isViewingOwnCompany =
    !!directory?.isOwnCompany || companyProfile.name === currentCompany.name
  const companyIri = companyProfile?.company?.['@id']

  const shouldShowInviteButtons =
    directory && !isViewingOwnCompany && !isCompanyAlreadyInPanel

  function buildDeclinedByNote() {
    if (panel?.status !== PANEL_STATUS.rejected) {
      return null
    }

    const declinedByUser = panel.requestRejectedByUser
    const declinedByCompanyName = declinedByUser?.company?.presentationName
    const declinedByUserName = declinedByUser?.user?.fullName

    // It's possible the user who declined the request has been removed from
    // the company in which case we may no longer have the details of the
    // declining user.
    if (!declinedByCompanyName && !declinedByUserName) {
      return null
    }

    const wasDeclinedByCurrentCompany =
      declinedByUser.company['@id'] === currentCompany['@id']

    const parts: string[] = [
      declinedByUserName && declinedByCompanyName && wasDeclinedByCurrentCompany
        ? // If the request was declined by the current company, show the name
          // of the declining user
          `Request was declined by ${declinedByUserName} (${declinedByCompanyName})`
        : // Otherwise, just show the name of the declining company
          `Request was declined by ${declinedByCompanyName}`,
    ]

    if (panel?.requestRejectedAt) {
      parts.push(
        `on ${formatDate(panel.requestRejectedAt, DATE_FORMATS.DAY_MONTH_YEAR)}`,
      )
    }

    return parts.join(' ')
  }

  const rejectedByNote = buildDeclinedByNote()

  function renderPanelStatusNote() {
    if (panel && panel.pendingActions.length > 0) {
      return (
        <PendingActionNote
          panel={panel}
          pendingActions={panel.pendingActions}
        />
      )
    }

    if (panelStatusStyles) {
      return (
        <div className="flex flex-col gap-2">
          <div
            className={cn(
              'text-md flex items-center min-w-30',
              panelStatusStyles.textClass,
            )}
          >
            {panelStatusStyles.icon}
            {panelStatusStyles.text}
          </div>

          {rejectedByNote && (
            <div className="text-xs text-muted-foreground/80 italic">
              {rejectedByNote}
            </div>
          )}
        </div>
      )
    }

    return <div /> // Empty div needed to keep layout consistent
  }

  const isCurrentCompanyFromCompany =
    panel && currentCompany['@id'] === panel.fromCompany['@id']

  const canCurrentCompanySend = panel
    ? isCurrentCompanyFromCompany
      ? panel.isFromCompanySendingEnabled
      : panel.isToCompanySendingEnabled
    : true

  const shouldShowToggleInternalRelationshipButton =
    !isViewingOwnCompany && !!panel && panel.status !== PANEL_STATUS.rejected

  return (
    <div
      className="flex grow flex-col flex-wrap gap-2 md:flex-row md:items-center
        md:justify-between md:gap-4"
    >
      {/* Left side */}
      {renderPanelStatusNote()}

      {/* Right side */}
      <div>
        {shouldShowToggleInternalRelationshipButton && (
          <ToggleInternalRelationship panel={panel} className="mb-2" />
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          {!isViewingOwnCompany && company && (
            <>
              {panelStatus === PANEL_STATUS.approved && companyIri && (
                <ReferClientButton
                  size="lg"
                  variant="primary"
                  toCompanyIri={companyIri}
                  disabled={!canCurrentCompanySend}
                  onCanRefer={() => {
                    navigate(
                      ROUTES.referClient({
                        searchParams: {
                          referralMethod: REFERRAL_METHOD.existingAdviser,
                          referralFirmId: company.id.toString(),
                        },
                      }),
                    )
                  }}
                >
                  Refer a client
                </ReferClientButton>
              )}

              <ContactCompanyDropdown company={companyProfile?.company} />
            </>
          )}

          {/* Add to professional network / invite buttons */}
          {shouldShowInviteButtons && (
            <ActionButton
              variant="primary"
              isProcessing={isAdding}
              showIsProcessingText={false}
              onClick={() => addToProfessionalNetwork(companyProfile)}
              size="lg"
            >
              <FontAwesomeIcon icon={faPlus} />
              {companyProfile.isRegistered
                ? 'Add to professional network'
                : 'Invite to join RQ'}
            </ActionButton>
          )}

          {/* Report button */}
          {reportSrc && (
            <Button asChild variant="black" size="lg">
              <a href={reportSrc} target="_blank" rel="noreferrer">
                <FontAwesomeIcon icon={faFilePdf} />
                Due Diligence Report
              </a>
            </Button>
          )}
        </div>
      </div>

      {!canCurrentCompanySend && (
        <Alert variant="warning" className="mt-2 sm:mt-0">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.75" />

          <AlertDescription>
            <p>
              <strong>Referral sending disabled:</strong>&nbsp;This relationship
              has been disabled by an admin - you are unable to refer clients at
              present.
            </p>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

export default CompanyActions
